import { RefreshCw } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { api, isApiError } from '@/api';
import type { CompliancePolicyVersion, PolicyDecisionRef } from '@/api/areas/governance';
import { Badge } from '@/components/Badge';
import { Callout, DataTable, Section, type Column } from '@/components/blocks';
import { DataError } from '@/components/DataError';
import { EntityRef } from '@/components/EntityRef';
import { KeyValue } from '@/components/KeyValue';
import { PageHeader, useDocumentTitle } from '@/components/PageHeader';
import { CopyText, EmptyState, Loading } from '@/components/States';
import { useRetained } from '@/components/Write';
import { Button } from '@/components/ui/button';
import { formatDate, formatDateTime } from '@/lib/format';
import { useApi } from '@/lib/useApi';
import { EnumBadge, humanize, useEnumLabel, When } from '../compliance/ui';
import { policyLabel } from '@/lib/labels';

const POLICY_STATUS = {
  DRAFT: { tone: 'warn' },
  APPROVED: { tone: 'info' },
  ACTIVE: { tone: 'ok' },
  RETIRED: { tone: 'neutral' },
  REJECTED: { tone: 'neutral' },
} as const;

const OUTCOME = { ALLOW: { tone: 'ok' }, ALLOW_WITH_REQUIRED_ACTIONS: { tone: 'warn' }, BLOCK: { tone: 'crit' } } as const;

function Json({ value }: { value: unknown }) {
  return <pre className="max-h-96 overflow-auto rounded-md border bg-muted/30 p-3 font-mono text-xs leading-relaxed">{JSON.stringify(value, null, 2)}</pre>;
}

/** `/compliance/policies/:key`: every version of one rule, and what it has decided. */
export function PolicyPage() {
  const { t } = useTranslation('compliance');
  const { t: tc } = useTranslation();
  const label = useEnumLabel();
  const { key = '' } = useParams();
  const result = useApi((signal) => api.getCompliancePolicy(key, { signal }), [key]);
  const p = useRetained(result.status === 'success' ? result.data : undefined);
  useDocumentTitle(p ? policyLabel(p.policy_key) : t('policies.detail.title'));
  const crumbs = [
    { label: t('crumbs.compliance') },
    { label: t('policies.title'), to: '/compliance/policies' },
    { label: p ? policyLabel(p.policy_key) : '…' },
  ];

  if (!p) {
    return (
      <>
        <PageHeader title={t('policies.detail.title')} crumbs={crumbs} />
        {result.status === 'error' ? (
          <DataError
            error={result.error}
            onRetry={isApiError(result.error) && result.error.status === 404 ? undefined : result.reload}
            title={t('policies.detail.loadError')}
          />
        ) : (
          <Loading rows={4} />
        )}
      </>
    );
  }

  const current = p.current;
  const versionCols: Column<CompliancePolicyVersion>[] = [
    {
      key: 'version',
      header: t('policies.detail.vcol.version'),
      cell: (v) => <span className="font-medium">{v.version}</span>,
    },
    {
      key: 'status',
      header: t('policies.detail.vcol.status'),
      // In force implies ACTIVE, so one badge per row says everything.
      cell: (v) => (v.in_force ? <Badge tone="ok">{t('policies.inForce')}</Badge> : <EnumBadge group="policyStatus" value={v.status} tones={POLICY_STATUS} />),
    },
    {
      key: 'effective',
      header: t('policies.detail.vcol.effective'),
      cell: (v) => (
        <span className="text-sm whitespace-nowrap">
          {t('policies.fromUntil', { from: formatDate(v.effective_from), until: v.effective_until ? formatDate(v.effective_until) : t('policies.openEnded') })}
        </span>
      ),
    },
    {
      key: 'approved',
      header: t('policies.detail.vcol.approved'),
      cell: (v) =>
        v.approved_by_name || v.approved_at ? (
          <div className="text-sm">
            {v.approved_by_name ?? '—'}
            {v.approved_at ? <div className="text-xs text-muted-foreground">{formatDate(v.approved_at)}</div> : null}
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      key: 'source',
      header: t('policies.detail.vcol.source'),
      cell: (v) => <EnumBadge group="policyOrigin" value={v.origin} tone="neutral" icon={false} />,
    },
  ];

  const decisionCols: Column<PolicyDecisionRef>[] = [
    { key: 'when', header: t('policies.detail.dcol.when'), cell: (d) => <When at={d.decided_at} /> },
    { key: 'operation', header: t('policies.detail.dcol.operation'), cell: (d) => <span title={d.operation}>{label('operation', d.operation)}</span> },
    {
      key: 'subject',
      header: t('policies.detail.dcol.subject'),
      cell: (d) =>
        d.domain_name ? (
          <EntityRef label={d.domain_name} to={`/domains/${encodeURIComponent(d.domain_name)}`} id={d.resource_id} />
        ) : (
          <span className="text-sm text-muted-foreground">{humanize(d.resource_type)}</span>
        ),
    },
    { key: 'outcome', header: t('policies.detail.dcol.outcome'), cell: (d) => <EnumBadge group="decisionOutcome" value={d.outcome} tones={OUTCOME} /> },
    { key: 'version', header: t('policies.detail.dcol.version'), cell: (d) => <code className="text-xs">{d.policy_version}</code> },
  ];

  return (
    <>
      <PageHeader
        title={policyLabel(p.policy_key)}
        crumbs={crumbs}
        subtitle={
          current ? (
            <span className="flex flex-wrap items-center gap-2">
              <code className="text-xs">{p.policy_key}</code>
              {current.in_force ? (
                <Badge tone="ok">{t('policies.inForce')}</Badge>
              ) : (
                <EnumBadge group="policyStatus" value={current.status} tones={POLICY_STATUS} />
              )}
              <EnumBadge group="policyKind" value={current.kind} tone="neutral" icon={false} />
              <span>{current.authority}</span>
            </span>
          ) : undefined
        }
        actions={
          <Button size="sm" variant="outline" onClick={result.reload}>
            <RefreshCw /> {tc('action.refresh')}
          </Button>
        }
      />
      <div className="space-y-4">
        {current?.status === 'DRAFT' ? <Callout tone="warn">{t('policies.draftNote')}</Callout> : null}
        {p.versions.length === 0 ? <Callout tone="info">{t('policies.detail.noVersions')}</Callout> : null}
        {p.file_versions.length === 0 ? <Callout tone="info">{t('policies.detail.noFiles')}</Callout> : null}

        {current ? (
          <Section title={t('policies.detail.facts')}>
            <KeyValue
              columns={3}
              rows={[
                [t('policies.detail.vcol.version'), current.version],
                [t('policies.detail.authority'), current.authority],
                [t('policies.detail.owner'), current.owner],
                [
                  t('policies.detail.vcol.effective'),
                  t('policies.fromUntil', {
                    from: formatDateTime(current.effective_from),
                    until: current.effective_until ? formatDateTime(current.effective_until) : t('policies.openEnded'),
                  }),
                ],
                [t('policies.detail.approvedBy'), current.approved_by_name],
                [t('policies.detail.approved'), current.approved_at ? formatDateTime(current.approved_at) : null],
                [t('policies.detail.source'), current.source_reference],
                [t('policies.detail.origin'), <EnumBadge key="o" group="policyOrigin" value={current.origin} tone="neutral" icon={false} />],
                [
                  t('policies.detail.checksum'),
                  current.content_sha256 ? <CopyText key="c" value={current.content_sha256} display={`${current.content_sha256.slice(0, 16)}…`} /> : null,
                ],
                current.id ? [t('policies.detail.id'), <CopyText key="id" value={current.id} />] : false,
                current.description ? [t('policies.detail.notes'), current.description] : false,
              ]}
            />
            {current.notes?.length ? (
              <ul className="mt-4 list-disc space-y-1 pl-5 text-sm text-muted-foreground">
                {current.notes.map((n) => (
                  <li key={n}>{n}</li>
                ))}
              </ul>
            ) : null}
            {current.content ? (
              <div className="mt-4 space-y-2">
                <h3 className="text-sm font-semibold">{t('policies.detail.content')}</h3>
                <Json value={current.content} />
              </div>
            ) : null}
            {current.parameters ? (
              <div className="mt-4 space-y-2">
                <h3 className="text-sm font-semibold">{t('policies.detail.parameters')}</h3>
                <Json value={current.parameters} />
              </div>
            ) : null}
          </Section>
        ) : null}

        {p.versions.length > 0 ? (
          <Section title={t('policies.detail.versions', { count: p.versions.length })} description={t('policies.detail.versionsHint')} flush>
            <DataTable columns={versionCols} rows={p.versions} rowKey={(v) => `db-${v.version}`} />
          </Section>
        ) : null}
        {p.file_versions.length > 0 ? (
          <Section title={t('policies.detail.files', { count: p.file_versions.length })} description={t('policies.detail.filesHint')} flush>
            <DataTable columns={versionCols} rows={p.file_versions} rowKey={(v) => `file-${v.version}`} />
          </Section>
        ) : null}
        <Section title={t('policies.detail.decisions', { count: p.decisions.length })} description={t('policies.detail.decisionsHint')} flush>
          <DataTable columns={decisionCols} rows={p.decisions} rowKey={(d) => d.id} empty={<EmptyState title={t('policies.detail.noDecisions')} />} />
        </Section>
      </div>
    </>
  );
}
